import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid2,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { capitalCase } from 'change-case';
import type * as ChartJs from 'chart.js';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ButtonGroup } from '@components/ButtonGroup';
import { LineChart } from '@components/Charts/Line';
import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import DataTable from '@components/DataTable';
import CustomRangeDialog from '@components/DateRange';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel';
import { RecordSourcePill } from '@components/RecordSourcePill';
import Selector from '@components/Selector';

import { DATATABLE_COLUMNS } from '@constants/columns';
import { LAB_TYPES } from '@constants/option-types/lab';

import { useGetLabResults } from '@hooks/api/lab-result';
import { useConversion } from '@hooks/useConversion';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import {
  DAYJS_MAP,
  Interval,
  MOBILE_INTERVAL_RANGES,
  WEB_INTERVAL_RANGES,
} from '@models/graph';

import { groupByIntervalAndAverage } from '@utils/chart';
import { convertUnit, getUnit } from '@utils/conversion';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'labResultsMain.title',
    defaultMessage: 'Lab Results',
  },
  first: {
    id: 'healthTracker.detail.kpi.start',
    defaultMessage: 'First',
  },
  last: {
    id: 'healthTracker.detail.kpi.end',
    defaultMessage: 'Last',
  },
  change: {
    id: 'healthTracker.detail.kpi.change',
    defaultMessage: 'Change',
  },
  high: {
    id: 'healthTracker.detail.kpi.high',
    defaultMessage: 'High',
  },
  low: {
    id: 'healthTracker.detail.kpi.low',
    defaultMessage: 'Low',
  },
  curationStatus: {
    id: 'GLOBAL.curationStatus',
    defaultMessage: 'Curation Status',
  },
  recordSource: {
    id: 'GLOBAL.recordSource',
    defaultMessage: 'Record Source',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
  moreDates: {
    id: 'healthTracker.filters.moreDates',
    defaultMessage: 'More Dates',
  },
  date: {
    id: 'healthTracker.filters.date',
    defaultMessage: 'Date',
  },
  custom: {
    id: 'healthTracker.filters.custom',
    defaultMessage: 'Custom',
  },
  lastDays: {
    id: 'healthTracker.filters.lastDays',
    defaultMessage:
      'Last {dayCount, plural, one {day} other {{dayCount, number} days}}',
  },
  lastWeeks: {
    id: 'healthTracker.filters.lastWeeks',
    defaultMessage:
      'Last {weekCount, plural, one {week} other {{weekCount, number} weeks}}',
  },
  lastMonths: {
    id: 'healthTracker.filters.lastMonths',
    defaultMessage:
      'Last {monthCount, plural, one {month} other {{monthCount, number} months}}',
  },
  lastYears: {
    id: 'healthTracker.filters.lastYears',
    defaultMessage:
      'Last {yearCount, plural, one {year} other {{yearCount, number} years}}',
  },
  days: { id: 'healthTracker.filters.days', defaultMessage: 'Days' },
  weeks: { id: 'healthTracker.filters.weeks', defaultMessage: 'Weeks' },
  months: { id: 'healthTracker.filters.months', defaultMessage: 'Months' },
  years: { id: 'healthTracker.filters.years', defaultMessage: 'Years' },
});

interface LabStatCardProps {
  label: string;
  value: React.ReactNode;
}

function LabStatCard({ label, value }: LabStatCardProps) {
  return (
    <Card
      sx={({ palette }) => ({
        height: 111,
        border: `1px solid ${palette.grey[200]}`,
        textAlign: 'center',
        alignContent: 'center',
        backgroundColor: palette.grey.A100,
      })}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography color="text.secondary" fontWeight={600} mx={1} variant="h5">
        {value ?? '-'}
      </Typography>
    </Card>
  );
}

const dataToGraph = (
  results: { value: number; testDate: Date }[],
  testName: string | null,
  color: string,
  minDate: Date,
  maxDate: Date
): ChartJs.ChartDataset<'line', ChartJs.Point[]> => {
  const dataPoints = results.map((r) => ({
    y: r.value,
    x: r.testDate.getTime(),
    pointStyle: 'circle' as const,
  }));
  // Bucket by the actual data's date span, not the selected range, so a wide
  // custom range doesn't collapse sparse data into overly coarse year buckets.
  const timestamps = results.map((r) => r.testDate.getTime());
  const dataMin = timestamps.length
    ? new Date(Math.min(...timestamps))
    : minDate;
  const dataMax = timestamps.length
    ? new Date(Math.max(...timestamps))
    : maxDate;
  return {
    data: groupByIntervalAndAverage(dataPoints, dataMin, dataMax),
    label: testName ?? '-',
    pointStyle: 'circle',
    pointRadius: 4,
    backgroundColor: color,
    borderColor: color,
  };
};

function LabResultTrend({
  type = '',
  test = '',
}: {
  type?: string;
  test?: string;
}) {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const {
    state: { isVitall, isPractitioner },
  } = useSession();
  const {
    state: { conversion },
  } = useConversion();
  const { palette } = useTheme();

  // There are cases where spaces need to be replaced with slashes in the test code
  const normalizedTest = test.replace(/ /u, '/');

  const testName = normalizedTest
    ? formatMessage({
        id: `labResults.result.${normalizedTest}`,
        defaultMessage: capitalCase(normalizedTest),
      })
    : null;

  const conversionTableName = type.split('|').at(1) ?? '';
  const conversionTable = conversion[conversionTableName];

  // Date range state — no filter by default; activates when user picks an interval
  const [maxDate, setMaxDate] = useState<Date>(() => dayjs().toDate());
  const [minDate, setMinDate] = useState<Date>(() => dayjs().toDate());
  const [interval, setCurrentInterval] = useState<Interval>(Interval.Custom);
  const [range, setRange] = useState<number>(0);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [isTableView, setIsTableView] = useState(true);

  const { data, loading } = useGetLabResults({ code: normalizedTest });

  // Transform all records into display-ready rows
  const allResults = useMemo(
    () =>
      data
        .map((r) => {
          const { unit } = r;
          const convertedUnit = getUnit(
            unit,
            conversionTable ? 'CON' : 'SI',
            normalizedTest
          );
          const testDate = r.getObservationDate();
          let { value } = r;
          let convertedValue = '';
          let rawStringValue = '';

          if (typeof value === 'string') {
            rawStringValue = value;
            value = parseFloat(value);
          }

          if (typeof value === 'number' && !isNaN(value)) {
            convertedValue = convertUnit(
              value,
              unit,
              conversionTable ? 'CON' : 'SI',
              3,
              normalizedTest,
              undefined,
              true
            );
          } else if (rawStringValue) {
            convertedValue = rawStringValue;
          } else {
            // Non-string, non-numeric values (e.g. a boolean from
            // valueBoolean) don't go through unit conversion, but
            // convertUnit still maps them (e.g. true -> 'Positive').
            convertedValue = convertUnit(
              value,
              unit,
              conversionTable ? 'CON' : 'SI',
              3,
              normalizedTest,
              undefined,
              true
            );
          }

          return {
            value: Number(convertedValue),
            convertedValue,
            unit: convertedUnit,
            testDate,
            source: r.facility ?? '-',
            curationStatus: r.curationStatus as CurationStatus | undefined,
            recordSource: r.recordSource as ResourceSourceType | undefined,
            id: r.id,
          };
        })
        .filter((r): r is typeof r & { testDate: Date } => Boolean(r.testDate)),
    [conversionTable, data, normalizedTest]
  );

  const numericResults = useMemo(
    () => allResults.filter((r) => !isNaN(r.value)),
    [allResults]
  );

  const qualitativeResults = useMemo(
    () => allResults.filter((r) => isNaN(r.value)),
    [allResults]
  );

  // Apply date range filter only when the user has picked one
  const filteredResults = useMemo(
    () =>
      isFilterActive
        ? numericResults.filter(
            (r) => r.testDate >= minDate && r.testDate <= maxDate
          )
        : numericResults,
    [isFilterActive, numericResults, minDate, maxDate]
  );

  const filteredQualitative = useMemo(
    () =>
      isFilterActive
        ? qualitativeResults.filter(
            (r) => r.testDate >= minDate && r.testDate <= maxDate
          )
        : qualitativeResults,
    [isFilterActive, qualitativeResults, minDate, maxDate]
  );

  // Effective date bounds for the chart — derived from data when no filter is active
  const effectiveMinDate = useMemo(() => {
    if (isFilterActive || !filteredResults.length) return minDate;
    return new Date(
      Math.min(...filteredResults.map((r) => r.testDate.getTime()))
    );
  }, [isFilterActive, filteredResults, minDate]);

  const effectiveMaxDate = useMemo(() => {
    if (isFilterActive || !filteredResults.length) return maxDate;
    return new Date(
      Math.max(...filteredResults.map((r) => r.testDate.getTime()))
    );
  }, [isFilterActive, filteredResults, maxDate]);

  // Compute KPI stats from filtered results (sorted asc by date)
  const kpis = useMemo(() => {
    if (!filteredResults.length) {
      return { first: null, last: null, change: null, high: null, low: null };
    }
    const sorted = [...filteredResults].sort(
      (a, b) => a.testDate.getTime() - b.testDate.getTime()
    );
    const firstItem = sorted.at(0);
    const lastItem = sorted.at(-1);
    if (!firstItem || !lastItem) {
      return { first: null, last: null, change: null, high: null, low: null };
    }
    const firstVal = firstItem.value;
    const lastVal = lastItem.value;
    const delta = lastVal - firstVal;
    const high = Math.max(...sorted.map((r) => r.value));
    const low = Math.min(...sorted.map((r) => r.value));
    const unit = firstItem.unit;
    const fmt = (v: number) => `${v} ${unit}`;
    return {
      first: fmt(firstVal),
      last: fmt(lastVal),
      change: (
        <>
          <Icon
            name={delta >= 0 ? 'arrow-up' : 'arrow-down'}
            size="xs"
            type="fas"
          />{' '}
          {parseFloat(Math.abs(delta).toFixed(3))} {unit}
        </>
      ),
      high: fmt(high),
      low: fmt(low),
    };
  }, [filteredResults]);

  // Date selector helpers
  const intervalTabs = useMemo(
    () => [
      { name: formatMessage(messages.days), value: Interval.Day },
      { name: formatMessage(messages.weeks), value: Interval.Week },
      { name: formatMessage(messages.months), value: Interval.Month },
      { name: formatMessage(messages.years), value: Interval.Year },
      { name: formatMessage(messages.custom), value: Interval.Custom },
    ],
    [formatMessage]
  );

  const selectedIntervalButtons = intervalTabs.map(
    ({ value }) => value === interval && range === 1
  );

  const getOptionLabel = (iv: Interval, val: number) => {
    switch (iv) {
      case Interval.Day:
        return formatMessage(messages.lastDays, { dayCount: val });
      case Interval.Week:
        return formatMessage(messages.lastWeeks, { weekCount: val });
      case Interval.Month:
        return formatMessage(messages.lastMonths, { monthCount: val });
      case Interval.Year:
        return formatMessage(messages.lastYears, { yearCount: val });
      default:
        return formatMessage(messages.custom);
    }
  };

  const selectOptions = Array.from(
    (isMobile ? MOBILE_INTERVAL_RANGES : WEB_INTERVAL_RANGES).entries()
  ).flatMap(([iv, ranges]) =>
    ranges.map((val) => (
      <option key={`${iv}-${val}`} value={`${iv}-${val}`}>
        {iv === Interval.Custom
          ? formatMessage(messages.custom)
          : getOptionLabel(iv, val)}
      </option>
    ))
  );

  const applyInterval = (
    newInterval: Interval,
    newRange: number,
    newMax: Date
  ) => {
    if (newInterval === Interval.Custom) return;
    const newMin = dayjs(newMax)
      .subtract(
        newRange,
        DAYJS_MAP[newInterval as Exclude<Interval, Interval.Custom>]
      )
      .toDate();
    setCurrentInterval(newInterval);
    setRange(newRange);
    setMinDate(newMin);
    setMaxDate(newMax);
    setIsFilterActive(true);
  };

  const handleIntervalButtonClick = (value: number | string) => {
    const newInterval = value as Interval;
    if (newInterval === Interval.Custom) {
      setCustomOpen(true);
      return;
    }
    applyInterval(newInterval, 1, dayjs().toDate());
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const [rawInterval, rawRange] = event.target.value.split('-');
    const newInterval = rawInterval as Interval;
    const newRange = parseInt(rawRange, 10);
    if (newInterval === Interval.Custom) {
      setCustomOpen(true);
      return;
    }
    if (!newRange) return;
    applyInterval(newInterval, newRange, maxDate);
  };

  const handleCustomApply = (start: Date, end: Date) => {
    setCurrentInterval(Interval.Custom);
    setRange(1);
    setMinDate(start);
    setMaxDate(end);
    setIsFilterActive(true);
    setCustomOpen(false);
  };

  // Table data
  const tableRows = filteredResults.map((r) => ({
    value: `${r.convertedValue} ${r.unit}`,
    date: r.testDate,
    source: r.source,
    curationStatus: r.curationStatus,
    recordSource: r.recordSource,
  }));

  const qualitativeTableRows = filteredQualitative.map((r) => ({
    value: r.convertedValue,
    date: r.testDate,
    source: r.source,
    curationStatus: r.curationStatus,
    recordSource: r.recordSource,
  }));

  const tableColumns = DATATABLE_COLUMNS.default
    .filter((c) => c.name !== 'clinicalNonClinical')
    .concat([
      {
        name: 'curationStatus',
        title: formatMessage(messages.curationStatus),
        render: (status: CurationStatus) => (
          <CurationStatusBadge status={status} />
        ),
        show: Boolean(isVitall),
      },
      {
        name: 'recordSource',
        title: formatMessage(messages.recordSource),
        render: (type: ResourceSourceType) => <RecordSourcePill type={type} />,
        show: Boolean(isVitall || isPractitioner),
      },
    ]);

  // Derive a chart-compatible interval from the actual date span for Custom ranges
  const chartInterval = useMemo(() => {
    if (interval !== Interval.Custom) return interval;
    const spanDays = dayjs(effectiveMaxDate).diff(
      dayjs(effectiveMinDate),
      'day'
    );
    if (spanDays > 365) return Interval.Year;
    if (spanDays > 30) return Interval.Month;
    if (spanDays > 7) return Interval.Week;
    return Interval.Day;
  }, [interval, effectiveMinDate, effectiveMaxDate]);

  const graphData = dataToGraph(
    filteredResults,
    testName,
    palette.purple.main,
    effectiveMinDate,
    effectiveMaxDate
  );

  const kpiCards = [
    { label: formatMessage(messages.first), value: kpis.first },
    { label: formatMessage(messages.last), value: kpis.last },
    { label: formatMessage(messages.change), value: kpis.change },
    { label: formatMessage(messages.high), value: kpis.high },
    { label: formatMessage(messages.low), value: kpis.low },
  ];

  return (
    <ContentLoader loading={loading}>
      {/* KPI stat cards */}
      <Box sx={{ flexGrow: 1, mb: 2 }}>
        <Grid2 container spacing={2}>
          {kpiCards.map(({ label, value }) => (
            <Grid2 key={label} size={{ xs: 6, sm: 2.4 }}>
              <LabStatCard label={label} value={value} />
            </Grid2>
          ))}
        </Grid2>
      </Box>

      {/* Chart / table area */}
      <Card>
        <CardContent
          sx={({ palette: p }) => ({ backgroundColor: p.grey['100'] })}
        >
          {/* Controls bar */}
          <Stack direction="row" justifyContent="space-between">
            {numericResults.length > 0 && (
              <Selector
                changeSelection={setIsTableView}
                isSelected={isTableView}
                leftLabel={
                  <Icon color="text.secondary" name="chart-simple" type="fas" />
                }
                rightLabel={
                  <Icon color="text.secondary" name="table" type="fas" />
                }
                size="small"
              />
            )}

            <Stack alignItems="center" direction="row" spacing={1}>
              {!isMobile && (
                <ButtonGroup
                  buttons={intervalTabs}
                  onClick={handleIntervalButtonClick}
                  selected={selectedIntervalButtons}
                />
              )}
              <FormControl size="small">
                <InputLabel
                  inputLabelProps={{ shrink: true }}
                  label={formatMessage(
                    isMobile ? messages.date : messages.moreDates
                  )}
                  style={{ color: 'text.secondary' }}
                />
                <Select
                  autoWidth
                  native
                  onChange={handleSelectChange}
                  input={
                    <OutlinedInput
                      size="small"
                      sx={{
                        minHeight: 30,
                        boxShadow: (theme) => theme.shadows[1],
                        height: 35,
                        fontSize: '14px',
                      }}
                    />
                  }
                  value={`${interval}-${range}`}
                >
                  <option value="">{formatMessage(messages.select)}</option>
                  {!isMobile && interval === Interval.Custom && (
                    <option value={`${Interval.Custom}-1`}>
                      {formatMessage(messages.custom)}
                    </option>
                  )}
                  {selectOptions}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>

        <CardContent
          sx={{
            padding: isTableView ? 0 : undefined,
            '&:last-child': { py: isTableView ? 0 : undefined, px: 0 },
          }}
        >
          {numericResults.length === 0 ? null : isTableView ? (
            <DataTable
              columns={tableColumns}
              conversionGroup={LAB_TYPES}
              conversionType={normalizedTest}
              data={tableRows}
              key={String(conversionTable)}
              showConversion
              showHead
              sortBy="date"
              sortOrder="desc"
            />
          ) : (
            <LineChart
              chartData={{
                datasets: [graphData],
              }}
              interval={chartInterval}
              maxDate={effectiveMaxDate}
              minDate={effectiveMinDate}
              plugins={[]}
            />
          )}
        </CardContent>
      </Card>

      {filteredQualitative.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent
            sx={{
              padding: 0,
              '&:last-child': { py: 0, px: 0 },
            }}
          >
            <DataTable
              columns={tableColumns}
              data={qualitativeTableRows}
              showHead
              sortBy="date"
              sortOrder="desc"
            />
          </CardContent>
        </Card>
      )}

      <CustomRangeDialog
        onApply={handleCustomApply}
        onCancel={() => setCustomOpen(false)}
        open={customOpen}
      />
    </ContentLoader>
  );
}

export default withErrorBoundary(LabResultTrend, {
  FallbackComponent: ErrorBoundaryFallback,
});
